/**
 * Global Utils
 */
let stayAIsubscribers = {};

function stayAIsubscribe(eventName, callback) {
  if (stayAIsubscribers[eventName] === undefined) {
    stayAIsubscribers[eventName] = [];
  }

  stayAIsubscribers[eventName] = [...stayAIsubscribers[eventName], callback];

  return function stayAIunsubscribe() {
    stayAIsubscribers[eventName] = stayAIsubscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function stayAIpublish(eventName, data) {
  if (stayAIsubscribers[eventName]) {
    const promises = stayAIsubscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

/**
 * Accessibility Utils
 */
const stayAItrapFocusHandlers = {};
function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  stayAItrapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', stayAItrapFocusHandlers.keydown);
  };

  stayAItrapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', stayAItrapFocusHandlers.keydown);
  };

  stayAItrapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', stayAItrapFocusHandlers.focusout);
  document.addEventListener('focusin', stayAItrapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', stayAItrapFocusHandlers.focusin);
  document.removeEventListener('focusout', stayAItrapFocusHandlers.focusout);
  document.removeEventListener('keydown', stayAItrapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {

  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}


class StayAICartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#StayAICartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Sets event listener for cart icon on navbar.
   * Replace '#cart-icon-bubble' with current icon's class name or id
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Opens cart drawer and traps focus based on triggering element.
   * @param {HTMLElement} triggerBy - Element that triggered this function (e.g. add to cart button)
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => { this.classList.add('animate', 'active');});

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.stay-ai-drawer__inner-empty')
          : document.getElementById('StayAICartDrawer');
        const focusElement = this.querySelector('.stay-ai-drawer__inner') || this.querySelector('.stay-ai-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('stay-ai-overflow-hidden');
  }

  /**
   * Closes cart drawer and removes trapped focus based on triggering element.
   */
  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('stay-ai-overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Re-renders cart content
   * @param {Object} parsedState - Cart state
   */
  async renderContents(parsedState) {
    const parsedStateId = parsedState?.id || parsedState?.items[0]?.id
    this.querySelector('.stay-ai-drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.stay-ai-drawer__inner').classList.remove('is-empty');
    this.productId = parsedStateId;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector('.stay-ai-drawer ' + section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#StayAICartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  /**
   * Selected section's inner HTML
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionInnerHTML(html, selector) {
    if (!selector || !html) return
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    
    if (element) {
      return element.innerHTML
    } else {
      return document.getElementById(selector.substring(1)).innerHTML
    }
  }

  /**
   * Cart sections that are re-rendered
   */
  getSectionsToRender() {
    return [
      {
        id: 'stay-ai-cart-drawer',
        selector: '#StayAICartDrawer',
      },
    ];
  }

  /**
   * Opens cart drawer and traps focus based on triggering element.
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionDOM(html, selector) {
    if (!selector || !html) return
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Sets active element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define('stay-ai-cart-drawer', StayAICartDrawer);

class StayAICartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('StayAICartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.loadEventListeners()
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  loadEventListeners() {
    const cartItemSubscriptionOptions = document.querySelectorAll('select[data-cart-selling-plan-toggle]');
    const cartItemSubscriptionButton = document.querySelectorAll('button[data-cart-selling-plan-toggle]');

    [...cartItemSubscriptionOptions].forEach((item) => {
      item.addEventListener(('change'), this.handleSellingPlanUpdate.bind(this))
    });
    [...cartItemSubscriptionButton].forEach((item) => {
      item.addEventListener('click', this.handleSellingPlanUpdate.bind(this))
    });
  }

  async handleSellingPlanUpdate(event) {
    event.preventDefault()
    const defaultSellingPlan = event.target.classList.contains('cart-item__selling-plan-subscribe')
    const parentElement = event.target.parentElement
    const variantKey = parentElement.dataset.variantKey
    const sellingPlan = defaultSellingPlan ? parentElement.dataset.availableSellingPlan : event.target.value

    await this.subscriptionToggle(variantKey, sellingPlan, event)
  }

  /**
   * Resets quantity input value
   * @param {String} id - Element index
   */
  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  /**
   * Check if quantity changes
   * @param {Event} event - Triggered event
   */
  onChange(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    
    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'StayAICartDrawer-CartItems',
        section: document.getElementById('StayAICartDrawer-CartItems').dataset.id,
        selector: '.stay-ai-drawer__contents',
      }
    ];
  }

  /**
   * Toggle between selling plan
   * @param {String} keys - Individual cart keys
   * @param {String} sellingPlanID - selling plan ID
   * @param {Event} event - Triggered event
   */
  async subscriptionToggle(keys, sellingPlanID, event) {
    if (!keys && !sellingPlanID) return

    if (keys) {
      const keysArray = keys.split('|').filter(Boolean)
      const variantIDs = keysArray.map((key) => key.split(':')[0])
      if (keysArray.length > 1) await this.deleteProducts(keysArray);

      await this.updateSellingPlan(keysArray, sellingPlanID, event)
    }
  }

  /**
   * Deletes products when updating selling plans on bundles.
   * *This is a solution to Shopify's inability to do batch cart updates.
   * @param {Array} keys - Cart keys
   */
  async deleteProducts(keys) {
    const updates = keys.reduce((acc, curr) => {
      acc[curr.toString()] = 0;
      return acc;
    }, {});

    const updateCart = await (await fetch(`/cart/update.js`, {
      method: "POST",
      headers: {
          "Content-Type": "application/json"
      },
      body: JSON.stringify({updates})
    })).json();
  }

  /**
   * Update selling plan on selected products in cart
   * @param {Array} keys - Cart keys
   * @param {String} sellingPlanID - selling plan ID
   * @param {Event} event - Triggered event
   */
  async updateSellingPlan(keys, sellingPlanID, event) {
    const mainEl = event.target.parentElement
    const itemQty = mainEl.dataset.cartQuantity
    const quantity = itemQty.split('|')
    const bundleMinMax = mainEl.dataset.bundleMinMax
    const bundleParent = mainEl.dataset.bundleParent
    const bundleID = this.getGuid();
    let drawer = document.querySelector('stay-ai-cart-drawer');
    let body = {}

    body = keys.length === 1 
    ? {
      'id': keys[0],
      'quantity': this.targetQuantity,
      'selling_plan': sellingPlanID
    }
    : {
      'items': keys.map((key, index) => {
        const id = key.split(':')[0]
        const data = {
          'id': id,
          'quantity': quantity[index],
          'selling_plan': sellingPlanID,
          'properties': {
            _bundle_min: bundleMinMax.split('|')[0],
            _bundle_max: bundleMinMax.split('|')[1],
            _bundle_id: bundleID
          }
        }
        if (id === bundleParent) {data.properties['_bundle_parent']= true}
        return data
      })
    }

    body.sections = drawer.getSectionsToRender().map((section) => section.id),
    body.section_url = window.location.pathname
    body = JSON.stringify(body)

    const route = keys.length > 1 ? stayAIRoutes.cart_add_url : stayAIRoutes.cart_change_url
    fetch(`${route}`, { ...fetchConfig(), ...{ body} })
      .then((response) => {
        return response.json();
      })
      .then((parsedState) => {
        if (drawer) {
          drawer.renderContents(parsedState);
        }
      })
      .catch((error) => {
        console.error("Error adding product to cart:", error);
      });
  }

  /**
   * Get unique ID for Bundle products
   * @returns {String}
   */
  getGuid() {
    let d = new Date().getTime();
    let d2 =  (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c == 'x' ? r: (r & 0x7 | 0x8)).toString(16);
    });
  }

  /**
   * Update product quantity with updated value
   * @param {String} line - Order of quantity element
   * @param {Number} quantity - Updated quantity
   * @param {Object} event - triggered event
   * @param {String} name - Name of selected element
   * @param {String} variantId - Variant ID
   */
  updateQuantity(line, quantity, event, name, variantId) {
    if (!line) return
    
    this.enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${stayAIRoutes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Stay-AI-Drawer-quantity-${line}`);

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('.stay-ai-drawer');

        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const lineItem = document.getElementById(`StayAICartDrawer-Item-${line}`);
        
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.stay-ai-drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.stay-ai-drawer .stay-ai-cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.stay-ai-drawer .stay-ai-cart-item__name'));
        }

        this.updateCartCount(parsedState.item_count)
        this.checkOrphanBundleItems(parsedState.items)
      })
      .catch((error) => {
        const errors = document.getElementById('cart-errors') || document.getElementById('StayAICartDrawer-CartErrors');
        errors.textContent = 'Error updating your cart'
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  updateCartCount(total, cartBody = null) {
    const cartHeaderCount = document.querySelector('#cart-icon-btn .Header__CartCount');

    if (!cartHeaderCount) return

    if (total) {
      cartHeaderCount.textContent = total;
      return
    }

    const cartItems = cartBody.querySelectorAll('.stay-ai-cart-item');

    const totalProducts = [...cartItems]?.reduce((acc, product) => {
      const productsTotal = product.querySelector('stay-ai-quantity-input .quantity__input')?.value || 0
      return acc + Number(productsTotal)
    }, 0);

    cartHeaderCount.textContent = totalProducts || 0
  }

  /**
   * Update product quantity with updated value
   * @param {String} line - Order of quantity element
   * @param {String} message - Error message to display
   */
  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`StayAICartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('StayAICartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * Selected section's inner HTML
   * @param {String} html - Cart state html
   * @param {String} selector - Element's class or id name
   * @returns {Element}
   */
  getSectionInnerHTML(html, selector) {
    if (!html || !selector) return
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * Temporarily disables menu cart item on update
   */
  enableLoading() {
    const mainCartItems = document.getElementById('StayAICartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * Enables temporarily disabled menu cart items
   */
  disableLoading() {
    const mainCartItems = document.getElementById('StayAICartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }

  /**
   * Checks for unlinked bundle items in cart
   * @param {Array} lineItems - Cart items
   */
  checkOrphanBundleItems = (lineItems) => {
    if (!lineItems || lineItems.length <= 0) return;
  
    let bundleItems = [...lineItems].map((item) => item?.properties?._bundle_id);
    let bundleIDs = bundleItems.filter((id) => id);
    let orphanBundleIDs = [];
    if (!bundleIDs || bundleIDs.length <= 0) return;
  
    bundleIDs.forEach((bundleID) => {
      let bundleMin = 3;
      let bundleMax = 20;
      let bundleCount = 0;
      const hasChildProduct = lineItems.filter((item) =>
        item.properties?._bundle_id == bundleID && !item?.properties?._bundle_parent,
      );
      const hasParentProduct = lineItems.find( (item) =>
        item.properties?._bundle_id == bundleID && item?.properties?._bundle_parent === true,
      );
  
      if (!hasChildProduct || hasChildProduct.length <= 0 || !hasParentProduct) {
        orphanBundleIDs.push(bundleID);
        return;
      }
  
      bundleMin = parseInt(hasParentProduct.properties?._bundle_min) || bundleMin;
      bundleMax = parseInt(hasParentProduct.properties?._bundle_max) || bundleMax;
  
      bundleCount = hasChildProduct.reduce((acc, item) => acc + parseInt(item.quantity), 0) || bundleCount;
      if (bundleCount < bundleMin || bundleCount > bundleMax) {
        orphanBundleIDs.push(bundleID);
      }
    });
  
    if (orphanBundleIDs.length <= 0) return;
  
    this.deleteOrphanBundleProducts(lineItems, orphanBundleIDs);
  };

  /**
   * Removes all unlinked bundle products
   * @param {Array} cart - Cart line items
   * @param {Array} orphanBundleIDs - Array of unlinked products to delete
   */
  deleteOrphanBundleProducts(cart, orphanBundleIDs) {
    let drawer = document.querySelector('stay-ai-cart-drawer');
    let orphanData = {
      updates: {},
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    if (drawer) {
      orphanData.sections = drawer.getSectionsToRender().map((section) => section.id);
    }

    orphanBundleIDs.forEach((bundleID) => {
      cart.map((item) => {
        if (!item || item?.properties?._bundle_id !== bundleID) {
          return;
        }
  
        orphanData["updates"][String(item.key)] = 0;
      });
    });

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "xmlhttprequest",
      },
      body: JSON.stringify(orphanData),
    })
    .then((response) => {
      return response.json();
    })
    .then((state) => {
      if (drawer) {
        drawer.renderContents(state);
      }
    })
    .catch((error) => {
      console.error("Error removing bundle product from cart:", error);
    });
  }
}
customElements.define('stay-ai-cart-items', StayAICartItems);

class StayAICartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('stay-ai-cart-items') || this.closest('stay-ai-cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}
customElements.define('stay-ai-cart-remove-button', StayAICartRemoveButton);

class StayAICartDrawerItems extends StayAICartItems {
  getSectionsToRender() {
    return [
      {
        id: 'StayAICartDrawer',
        section: 'stay-ai-cart-drawer',
        selector: '.stay-ai-drawer__inner',
      }
    ];
  }
}
customElements.define('stay-ai-cart-drawer-items', StayAICartDrawerItems);

class StayAIQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('.quantity__button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.validateQtyRules();
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('stay-ai-quantity-input', StayAIQuantityInput);
